import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { photo, photoSrcSet } from '../assets/photos';
import Seo from '../components/Seo';

const Reveal = ({
  children, delay = 0, className = '', y = 24,
}: {
  children: React.ReactNode; delay?: number; className?: string; y?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Services = () => {

  const services = [
    {
      label: 'Photography',
      title: 'Everlasting frames',
      description: 'We combine candid documentary storytelling with carefully directed moments to capture your wedding authentically and beautifully. You don\'t need to know your angles — we\'ll guide you every step of the way so you can simply be present and enjoy your day.',
      features: [
        'Complimentary engagement session',
        '200km travel included',
        '9 hours of wedding day coverage',
        'Preview gallery delivered within 24 hours',
        '800 professionally edited photos',
        'High-resolution, print-ready files',
        'Online gallery for easy sharing',
        'USB with all edited images',
      ],
      image: 'sarah-simon/bridal-1.jpg',
    },
    {
      label: 'Videography',
      title: 'Your story in motion',
      description: 'Your love story preserved not just as it looked, but as it felt — your vows, the laughter, the tears of joy, and the music that filled the room. A film returns the full sensory experience of your wedding day, again and again.',
      features: [
        'Complimentary engagement session video',
        '200km travel included',
        '9 hours of wedding day coverage',
        'Preview reel delivered within 24 hours',
        '3-minute cinematic trailer',
        '8-minute highlight film',
        'Full ceremony & reception edit',
        'Professional colour grading & sound design',
        'USB with all footage',
      ],
      image: 'cezanne-dewyk/5.jpg',
    },
    {
      label: 'Photography & Videography',
      title: 'The complete story',
      description: 'Photos capture the split-second moments you\'ll print and cherish forever, while film preserves your vows, the motion, the soundtrack, and the full emotion of your day. Together they tell a story no single medium can — and your wedding day can never be recreated.',
      features: [
        'Complimentary engagement session (photo & video)',
        '200–300km travel included',
        '8–10 hours of wedding day coverage',
        'Preview photos within 24 hours',
        '600–900 professionally edited photos',
        'Drone footage',
        'Preview video reel within 48 hours',
        '4–5 minute cinematic trailer',
        'Full ceremony & reception edit (Premium)',
        'Online gallery & USB delivery',
      ],
      image: 'mel-brandon/5.jpg',
      featured: true,
    },
    {
      label: 'Engagements',
      title: 'Before the big day',
      description: 'A relaxed, pressure-free session to celebrate your engagement and get comfortable in front of the camera. It\'s also the perfect opportunity for us to get to know each other and understand your dynamic as a couple. Included complimentary in all our wedding packages.',
      features: [
        'Included complimentary in all wedding packages',
        'Relaxed, guided — no posing experience needed',
        'Location guidance and suggestions provided',
        'Styling tips and outfit advice',
        'Online gallery within 7 working days',
        'High-resolution edited images',
      ],
      image: 'lebo-siya/5.jpg',
    },
  ];

  const packages = [
    {
      name: 'Premium Photo',
      desc: 'Comprehensive wedding photography',
      features: [
        'Engagement session',
        '200km travel',
        '9 hours coverage',
        'Preview within 24 hrs',
        '800 edited photos',
        'Online gallery & USB',
      ],
      highlight: false,
    },
    {
      name: 'Premium Video',
      desc: 'Cinematic wedding film',
      features: [
        'Engagement session video',
        '200km travel',
        '9 hours coverage',
        'Preview reel within 24 hrs',
        '3-min trailer · 8-min film',
        'Full ceremony & reception edit',
        'USB delivery',
      ],
      highlight: false,
    },
    {
      name: 'Signature Combo',
      desc: 'Photos & film — our most popular',
      features: [
        'Engagement session (photo & video)',
        '200km travel',
        '8 hours coverage',
        'Preview photos within 24 hrs',
        '600 edited photos · Drone footage',
        '5-min cinematic trailer',
        'Online gallery & USB',
      ],
      highlight: true,
    },
    {
      name: 'Premium Combo',
      desc: 'The ultimate full-day experience',
      features: [
        'Engagement session (photo & video)',
        '300km travel',
        '10 hours coverage',
        'Preview photos within 24 hrs',
        '900 edited photos · Drone footage',
        '4-min trailer · 10-min highlight film',
        'Full ceremony & reception edit',
        'Online gallery & USB',
      ],
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen bg-ivory overflow-x-hidden">
      <Seo route="/services" />

      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="page-header bg-bone px-8 md:px-14 lg:px-20">
        <div className="max-w-screen-xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="label-sm text-mist mb-5"
          >
            What We Offer
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="heading-display text-ink text-[clamp(3rem,7vw,7rem)] mb-6"
          >
            Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="body-light max-w-xl"
          >
            Every love story is different. We offer photography, videography, and combined packages tailored to preserve every dimension of your day.
          </motion.p>
        </div>
      </div>

      {/* ── Services — editorial alternating layout ──────────────── */}
      <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
        {services.map((service, i) => (
          <div
            key={service.label}
            className={`py-20 md:py-32 border-b border-silver/40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center ${
              i % 2 === 1 ? '' : ''
            }`}
          >
            {/* Image */}
            <Reveal
              delay={0}
              className={`lg:col-span-6 overflow-hidden ${i % 2 === 1 ? 'lg:order-2' : ''}`}
            >
              <div className="overflow-hidden">
                <img loading="lazy" decoding="async"
                  src={photo(service.image)}
                  srcSet={photoSrcSet(service.image)}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  alt={`${service.label} — wedding services by George & Co.`}
                  className="w-full h-[50vw] max-h-[560px] min-h-[260px] object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                />
              </div>
              {service.featured && (
                <p className="label-sm text-champagne-dark mt-4">Most popular choice</p>
              )}
            </Reveal>

            {/* Text */}
            <div className={`lg:col-span-6 ${i % 2 === 1 ? 'lg:order-1 lg:pr-8' : 'lg:pl-8'}`}>
              <Reveal delay={0.1}>
                <p className="label-sm text-mist mb-4">{service.label}</p>
                <h2 className="heading-display text-ink text-[clamp(2rem,3.5vw,3.5rem)] mb-6">
                  {service.title}
                </h2>
                <p className="body-light mb-10">{service.description}</p>
              </Reveal>

              <Reveal delay={0.2}>
                <ul className="space-y-2.5 mb-10">
                  {service.features.map(f => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-[7px] w-1 h-1 rounded-full bg-champagne flex-shrink-0" />
                      <span className="label-sm text-mist">{f}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={0.25}>
                <Link to="/contact" className="link-underline text-ink">
                  Inquire <span className="ml-1">→</span>
                </Link>
              </Reveal>
            </div>
          </div>
        ))}
      </div>

      {/* ── Packages ────────────────────────────────────────────── */}
      <section className="bg-bone py-24 md:py-36">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">Packages</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="heading-display text-ink text-[clamp(2.4rem,5vw,5rem)] mb-16 md:mb-20">
              Choose your package
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-silver/30">
            {packages.map((pkg, i) => (
              <Reveal key={pkg.name} delay={i * 0.07}>
                <div
                  className={`p-8 md:p-10 h-full flex flex-col ${
                    pkg.highlight ? 'bg-ink text-white' : 'bg-bone'
                  }`}
                >
                  {pkg.highlight && (
                    <p className="label-sm text-champagne mb-4">Most Popular</p>
                  )}
                  <h3 className={`heading-display text-2xl mb-2 ${pkg.highlight ? 'text-white' : 'text-ink'}`}>
                    {pkg.name}
                  </h3>
                  <p className={`label-sm mb-8 ${pkg.highlight ? 'text-white/60' : 'text-mist'}`}>
                    {pkg.desc}
                  </p>

                  <ul className="space-y-3 flex-1">
                    {pkg.features.map(f => (
                      <li key={f} className="flex items-start gap-3">
                        <span className={`mt-[7px] w-1 h-1 rounded-full flex-shrink-0 ${pkg.highlight ? 'bg-champagne' : 'bg-silver'}`} />
                        <span className={`label-sm ${pkg.highlight ? 'text-white/55' : 'text-mist'}`}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10">
                    <Link
                      to="/contact"
                      className={`link-underline ${pkg.highlight ? 'text-white/60' : 'text-ink'}`}
                    >
                      Inquire <span className="ml-1">→</span>
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why choose us ────────────────────────────────────────── */}
      <section className="bg-ivory py-24 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">Why George & Co.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="heading-display text-ink text-[clamp(2.4rem,5vw,5rem)] mb-16 md:mb-20">
              The difference
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
            {[
              {
                n: '01',
                title: 'At ease in front of the lens',
                desc: 'We make you feel comfortable so naturally that you almost forget we\'re there — allowing genuine moments to unfold.',
              },
              {
                n: '02',
                title: 'Seamlessly orchestrated',
                desc: 'We act as silent partners on the day, keeping the schedule on track without being intrusive or disruptive.',
              },
              {
                n: '03',
                title: 'Guided every step',
                desc: 'From first inquiry to final delivery, we guide you through the entire process with insider tips and care.',
              },
              {
                n: '04',
                title: 'Limited bookings',
                desc: 'We take on a limited number of weddings each year to give every couple the focus and attention they deserve.',
              },
            ].map((item, i) => (
              <Reveal key={item.n} delay={i * 0.08}>
                <div className="flex gap-6 items-start">
                  <span className="label-sm text-mist pt-1 flex-shrink-0 w-8">{item.n}</span>
                  <div>
                    <h3 className="heading-display text-ink text-xl md:text-2xl mb-3">{item.title}</h3>
                    <p className="body-light text-sm md:text-[0.9375rem]">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="bg-ink py-24 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <Reveal>
            <p className="label-sm text-champagne mb-6">Ready to begin?</p>
            <h2 className="heading-italic text-white text-[clamp(2rem,4.5vw,4.5rem)] leading-[1.05]">
              Your story is waiting<br />to be told.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/contact" className="link-underline text-white/55">
              Begin your inquiry <span className="ml-1">→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
